/**
 * "Space Math": maths questions for Tahun 1-6 (KSSR) where each answer is a space rock.
 * Questions are written with symbols only (12 × 7, 40 − 15, 25% × 80), so they read the same in Bahasa Melayu and English.
 */
export type AngkasaQ = { text: string; answer: string; options: string[]; year: number };

type Rnd = () => number;
const int = (r: Rnd, lo: number, hi: number) => lo + Math.floor(r() * (hi - lo + 1));
const pick = <T,>(r: Rnd, a: readonly T[]): T => a[Math.floor(r() * a.length)]!;
const shuffled = <T,>(r: Rnd, a: readonly T[]): T[] => {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [b[i], b[j]] = [b[j]!, b[i]!]; }
  return b;
};
const fmt = (n: number, dp: number) => (dp ? n.toFixed(dp) : String(Math.round(n)));

type Raw = { text: string; value: number; dp?: number };

function make(year: number, r: Rnd): Raw {
  const kind = int(r, 0, 5);
  switch (Math.min(6, Math.max(1, year))) {
    case 1: {
      if (kind < 2) { const a = int(r, 1, 12), b = int(r, 1, 20 - a); return { text: `${a} + ${b}`, value: a + b }; }
      if (kind < 4) { const a = int(r, 5, 20), b = int(r, 1, a - 1); return { text: `${a} − ${b}`, value: a - b }; }
      const a = int(r, 1, 9), b = int(r, 1, 20 - a); return { text: `${a} + □ = ${a + b}`, value: b };
    }
    case 2: {
      if (kind === 0) { const a = int(r, 10, 60), b = int(r, 10, 99 - a); return { text: `${a} + ${b}`, value: a + b }; }
      if (kind === 1) { const a = int(r, 30, 99), b = int(r, 10, a - 1); return { text: `${a} − ${b}`, value: a - b }; }
      if (kind === 2) { const a = pick(r, [2, 5, 10]), b = int(r, 1, 10); return { text: `${a} × ${b}`, value: a * b }; }
      if (kind === 3) { const d = pick(r, [2, 5, 10]), q = int(r, 1, 10); return { text: `${d * q} ÷ ${d}`, value: q }; }
      const a = int(r, 10, 50), b = int(r, 5, 40); return { text: `${a} + □ = ${a + b}`, value: b };
    }
    case 3: {
      if (kind === 0) { const a = int(r, 100, 700), b = int(r, 10, 250); return { text: `${a} + ${b}`, value: a + b }; }
      if (kind === 1) { const a = int(r, 300, 999), b = int(r, 10, 250); return { text: `${a} − ${b}`, value: a - b }; }
      if (kind < 4) { const a = int(r, 2, 9), b = int(r, 2, 10); return { text: `${a} × ${b}`, value: a * b }; }
      const d = int(r, 2, 9), q = int(r, 2, 10); return { text: `${d * q} ÷ ${d}`, value: q };
    }
    case 4: {
      if (kind === 0) { const a = int(r, 1000, 6000), b = int(r, 100, 3000); return { text: `${a} + ${b}`, value: a + b }; }
      if (kind === 1) { const a = int(r, 3000, 9999), b = int(r, 100, 2999); return { text: `${a} − ${b}`, value: a - b }; }
      if (kind === 2) { const a = int(r, 11, 99), b = int(r, 2, 9); return { text: `${a} × ${b}`, value: a * b }; }
      if (kind === 3) { const d = int(r, 3, 9), q = int(r, 12, 99); return { text: `${d * q} ÷ ${d}`, value: q }; }
      const a = int(r, 2, 9), b = int(r, 1, 9), c = int(r, 2, 6); return { text: `(${a} + ${b}) × ${c}`, value: (a + b) * c };
    }
    case 5: {
      if (kind === 0) { const a = int(r, 12, 35), b = int(r, 11, 25); return { text: `${a} × ${b}`, value: a * b }; }
      if (kind === 1) { const d = int(r, 11, 25), q = int(r, 11, 40); return { text: `${d * q} ÷ ${d}`, value: q }; }
      if (kind === 2) { const p = pick(r, [10, 20, 25, 50]), n = pick(r, [40, 60, 80, 120, 200, 240]); return { text: `${p}% × ${n}`, value: (p * n) / 100 }; }
      if (kind === 3) { const a = int(r, 11, 99) / 10, b = int(r, 11, 99) / 10; return { text: `${a.toFixed(1)} + ${b.toFixed(1)}`, value: Math.round((a + b) * 10) / 10, dp: 1 }; }
      if (kind === 4) { const a = int(r, 20, 99) / 10, b = int(r, 11, Math.round(a * 10) - 1) / 10; return { text: `${a.toFixed(1)} − ${b.toFixed(1)}`, value: Math.round((a - b) * 10) / 10, dp: 1 }; }
      const a = int(r, 1000, 9000), b = int(r, 1000, 9000); return { text: `${a} + ${b}`, value: a + b };
    }
    default: {
      if (kind === 0) { const a = int(r, 2, 20), b = int(r, 2, 9), c = int(r, 2, 9); return { text: `${a} + ${b} × ${c}`, value: a + b * c }; }
      if (kind === 1) { const p = pick(r, [15, 30, 35, 45, 75]), n = pick(r, [20, 40, 60, 80, 120, 200]); return { text: `${p}% × ${n}`, value: (p * n) / 100 }; }
      if (kind === 2) { const n = int(r, 4, 15); return { text: `${n}²`, value: n * n }; }
      if (kind === 3) { const f = pick(r, [["½", 2], ["¼", 4], ["¾", 4]] as const), m = int(r, 2, 12); const num = f[0] === "¾" ? 3 : 1; return { text: `${f[0]} × ${f[1] * m}`, value: num * m }; }
      if (kind === 4) { const a = int(r, 11, 99) / 10, c = int(r, 2, 9); return { text: `${a.toFixed(1)} × ${c}`, value: Math.round(a * c * 10) / 10, dp: 1 }; }
      const a = int(r, 2, 9), b = int(r, 1, 9), c = int(r, 2, 6); return { text: `(${a * c + b * c}) ÷ ${c}`, value: a + b };
    }
  }
}

/** Three believable wrong answers: close in size and written the same way as the right one. */
function wrongOnes(value: number, dp: number, r: Rnd): number[] {
  const step = dp ? 0.1 : 1;
  const out = new Set<string>(); const right = fmt(value, dp);
  const cand = [step, -step, 2 * step, -2 * step, 10 * step, -10 * step, 3 * step, -3 * step, 5 * step, -5 * step, value >= 20 ? Math.round(value / 10) * step : 4 * step, -4 * step];
  for (const d of shuffled(r, cand)) {
    const v = Math.round((value + d) * 100) / 100;
    if (v < 0 || (!dp && !Number.isInteger(v))) continue;
    const s = fmt(v, dp);
    if (s !== right) out.add(s);
    if (out.size === 3) break;
  }
  for (let k = 1; out.size < 3; k++) out.add(fmt(value + (k + 5) * step, dp));
  return [...out].map(Number);
}

/** One question: four different answers, one right. */
export function angkasaQuestion(year: number, r: Rnd = Math.random): AngkasaQ {
  const q = make(year, r);
  const dp = q.dp ?? 0;
  const answer = fmt(q.value, dp);
  const options = shuffled(r, [answer, ...wrongOnes(q.value, dp, r).map((n) => fmt(n, dp))]);
  return { text: q.text, answer, options, year };
}

/** A round of `n` different questions. */
export function angkasaRound(year: number, n = 10, r: Rnd = Math.random): AngkasaQ[] {
  const out: AngkasaQ[] = []; const seen = new Set<string>();
  for (let guard = 0; out.length < n && guard < 400; guard++) {
    const q = angkasaQuestion(year, r);
    if (seen.has(q.text)) continue;
    seen.add(q.text); out.push(q);
  }
  return out;
}
